import type { Breakpoint } from '@ctw/config/zui';
import { DrawerContent } from '@ctw/shared/components/containers/_internal/drawer-content';
import { Drawer, type DrawerProps } from '@ctw/shared/components/containers/drawer';
import { useRegisterPresence } from '@ctw/shared/context/zui-provider';
import { useBreakpointQuery } from '@ctw/shared/hooks/breakpoints';
import { twx } from '@ctw/shared/utils/tailwind';
import { usePrevious } from '@uidotdev/usehooks';
import { useLayoutEffect, useMemo, useState } from 'react';

interface DockableDrawerContentBreakpoints {
  docked: Breakpoint;
}

const contentBreakpoints: DockableDrawerContentBreakpoints = {
  docked: 'lg',
};

type DockableDrawerDockingMode = 'always' | 'responsive';
type DockableDrawerDockDisplay = 'aside' | 'detail';

export interface DockableDrawerProps extends DrawerProps {
  dockingMode?: DockableDrawerDockingMode;
  dockDisplay?: DockableDrawerDockDisplay;
}

export const DockableDrawer = ({
  isOpen,
  dockingMode = 'responsive',
  dockDisplay = 'aside',
  ...drawerProps
}: DockableDrawerProps) => {
  const { breakpoints } = useBreakpointQuery();
  const isDockable = useMemo(
    () => dockingMode === 'always' || breakpoints.isAtLeast[contentBreakpoints.docked],
    [breakpoints, dockingMode],
  );
  const isDocked = useMemo(() => isDockable && isOpen, [isDockable, isOpen]);
  const [isHidden, setIsHidden] = useState(true);
  const previousIsDocked = usePrevious(isDocked);

  useRegisterPresence('dockedDrawer', isDocked);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useLayoutEffect(() => {
    if (isDocked && !previousIsDocked) {
      setIsHidden(false);
      drawerProps.onOpen?.();
      setTimeout(() => {
        drawerProps.onAfterOpen?.();
      }, 300);
    } else if (!isDocked && previousIsDocked) {
      setTimeout(() => {
        drawerProps.onAfterClose?.();
        setIsHidden(true);
      }, 300);
    }
  }, [isDocked, previousIsDocked]);

  if (isDockable) {
    return (
      <aside
        className={twx('h-full min-h-full overflow-hidden transition-all duration-300', {
          'h-full w-[320px] max-w-[320px] min-[1100px]:w-[450px] min-[1100px]:max-w-[450px]':
            isDocked && dockDisplay === 'aside',
          'h-full w-full min-w-full min-[1100px]:w-[450px] min-[1100px]:max-w-[450px]':
            isDocked && dockDisplay === 'detail',
          'h-0 w-0 max-w-0 opacity-0': !isDocked,
        })}
      >
        <div
          className={twx(
            'h-full max-h-full min-h-full overflow-visible transition-all duration-300',
            {
              hidden: isHidden,
              'h-full w-[320px] min-w-[320px] max-w-[320px] min-[1100px]:w-[450px] min-[1100px]:min-w-[450px] min-[1100px]:max-w-[450px]':
                isDocked && dockDisplay === 'aside',
              'h-full w-full min-w-full min-[1100px]:w-[450px] min-[1100px]:min-w-[450px] min-[1100px]:max-w-[450px]':
                isDocked && dockDisplay === 'detail',
            },
          )}
        >
          <DrawerContent isDocked={isDocked} {...drawerProps} />
        </div>
      </aside>
    );
  }

  return <Drawer isOpen={isOpen} {...drawerProps} />;
};
